import { Building } from 'apolloClient/types';
import { Listing } from 'apolloClient/types/Units';
import { getListingUnitIdentifier } from './utils';
import { streetDirectionFormat } from './buildingUtils';

/**
 * The property block of the FUB Registration event.
 *
 * The backend used to derive it from the visitor's last activity row and a unit
 * lookup; both queries were dropped, so the page now states which listing the
 * visitor was looking at when they signed up.
 *
 * The register popup lives next to `<Component/>` in `_app`, not inside the unit
 * page, so it cannot read `UnitPageContext`. The unit page publishes here on
 * mount and clears on unmount instead — outside React because nothing renders
 * from it.
 */
export type RegistrationProperty = {
  street: string;
  city: string;
  state: string;
  code: string;
  price: string;
  forRent: string;
  url: string;
  // Not part of FUB's property block: the backend turns these into lead tags,
  // the same text Contact Us sends (see getContactFormContextData).
  buildingName?: string;
  neighborhood?: string;
};

export function buildRegistrationProperty({
  listing,
  building,
  cityName,
  url,
}: {
  listing: Listing | undefined;
  building?: Building;
  cityName: string | undefined;
  url: string;
}): RegistrationProperty | null {
  if (!listing?.address) return null;

  const { streetNumber, streetDirection, streetName, streetType, zipCode } =
    listing.address;
  const unitNumber = getListingUnitIdentifier(listing);
  const direction = streetDirectionFormat(streetDirection);
  // FUB shares one property record per unit across events and every event
  // overwrites its street, so dropping the type here strips it from the
  // unit's Contact Us and Viewed Property items too.
  const type = streetType && streetType !== 'Unknown' ? streetType : '';
  const buildingName = building?.name?.trim();
  const neighborhood = building?.neighborhood?.data?.attributes?.name?.trim();

  return {
    street: [streetNumber, direction, streetName, type]
      .filter(Boolean)
      .join(' ')
      .concat(unitNumber ? ` # ${unitNumber}` : '')
      .replace(/\s+/g, ' ')
      .trim(),
    city: cityName ?? '',
    state: 'FL',
    code: zipCode ?? '',
    price: listing.price?.toString() ?? '0',
    forRent: listing.propertyTypeCode === 'R' ? '1' : '0',
    url,
    ...(buildingName ? { buildingName } : {}),
    ...(neighborhood ? { neighborhood } : {}),
  };
}

let currentProperty: RegistrationProperty | null = null;

export function setRegistrationProperty(
  property: RegistrationProperty | null
): void {
  currentProperty = property;
}

export function getRegistrationProperty(): RegistrationProperty | null {
  return currentProperty;
}
